<template>
<div class="ai-access-keys">
  <!-- The tab title ("AI access keys") is rendered by UserProfile's
       .title-label above this pane — no h1 here or it doubles up. -->
  <p class="lede">POP writes with AI. By default that comes out of your POP credits and you don't have to set anything up. If you'd rather pay OpenAI or Anthropic directly, add your key here.</p>

  <!-- "Right now" status strip -->
  <div class="status-strip" :class="{ credits: !activeLabels.length }">
    <span class="ss-dot"></span>
    <div>
      <div class="ss-title">{{ stripTitle }}</div>
      <div class="ss-sub">{{ stripSub }}</div>
    </div>
  </div>

  <details class="explainer">
    <summary>Not sure whether to add a key? <span class="chev"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"></path></svg></span></summary>
    <div class="ex-body">
      <div class="ex-col">
        <h4>Stay on POP credits</h4>
        <ul>
          <li>Nothing to set up, one bill from POP</li>
          <li>Best if you run a handful of pages a month</li>
        </ul>
      </div>
      <div class="ex-col">
        <h4>Use your own key</h4>
        <ul>
          <li>OpenAI or Anthropic bills you for the words</li>
          <li>POP charges a flat <strong>2 credits per AI Writer run</strong>, whether the article is 500 words or 5,000 (plus 1 credit per image if your Claude API key is enabled)</li>
          <li><strong>Schema generation costs 0 credits</strong></li>
          <li>Usually cheaper at high volume, and you see usage in your AI account</li>
        </ul>
      </div>
    </div>
  </details>

  <!-- Provider cards -->
  <section v-for="p in providers" :key="p.id" class="card">
    <div class="card-head">
      <span class="card-logo" :style="{ background: p.logoColor }">{{ p.badge }}</span>
      <h3>{{ p.keyLabel }}</h3>
      <span class="card-state" :class="stateChip(p.id).cls">{{ stateChip(p.id).label }}</span>
    </div>
    <p class="card-sub">Powers the {{ p.modelsLabel }} models in AI Writer, Watchdog and bulk runs.</p>

    <div class="empty" v-if="!showLive(p.id)">
      <h4>No {{ p.keyLabel }} added</h4>
      <p>{{ p.modelsLabel }} models still work without one: they just come out of your POP credits at standard pricing.</p>
      <div class="steps">
        <div class="step"><span class="n">1</span><span>Sign in at <a :href="p.consoleUrl" target="_blank" rel="noopener">{{ p.consoleLabel }}</a> and create {{ p.createWhat }}.</span></div>
        <div class="step"><span class="n">2</span><span>Copy it once. {{ p.company }} won't show it again.</span></div>
        <div class="step"><span class="n">3</span><span>Paste it here and save. POP stores it on your profile and never shows it to sub-accounts.</span></div>
      </div>
      <button class="btn-add" type="button" @click="startAdding(p)">Add {{ p.keyLabel }}</button>
    </div>

    <div v-else>
      <div class="field">
        <label :for="p.id + 'Key'">{{ p.inputLabel }}</label>
        <div class="key-row">
          <input class="key-input" :class="{ bad: cards[p.id].bad }"
            :id="p.id + 'Key'" :ref="p.id + 'Input'" type="text" spellcheck="false" autocomplete="off"
            :placeholder="'Starts with ' + p.prefix"
            :value="displayValue(p.id)"
            :readonly="cards[p.id].masked"
            @input="onKeyInput(p, $event)">
          <button class="btn-ghost" type="button" :disabled="!cards[p.id].real" @click="toggleMask(p)">{{ cards[p.id].masked ? 'Show' : 'Hide' }}</button>
          <button class="btn-ghost" type="button" :disabled="!cards[p.id].real || cards[p.id].testing" @click="testKey(p)">{{ cards[p.id].testing ? 'Testing…' : 'Test key' }}</button>
        </div>
        <div class="field-help">From <a :href="p.consoleUrl" target="_blank" rel="noopener">{{ p.consoleLabel }}</a>. Stored on your POP profile, hidden from sub-accounts, and you can remove it any time.</div>
        <div class="inline-msg" :class="cards[p.id].msg.kind" v-if="cards[p.id].msg"><span class="ic">{{ cards[p.id].msg.kind === 'ok' ? '✓' : '!' }}</span><span>{{ cards[p.id].msg.text }}</span></div>
      </div>

      <div class="toggle-row" :class="{ off: !cards[p.id].toggleOn }">
        <button class="toggle" :class="{ on: cards[p.id].toggleOn }" type="button" aria-label="Use this key"
          :style="{ opacity: cards[p.id].real ? '1' : '.45', cursor: cards[p.id].real ? 'pointer' : 'not-allowed' }"
          @click="onToggle(p)"></button>
        <span>
          <span class="tl">Use this key for AI features</span>
          <span class="th">On: {{ p.company }} bills you for usage and POP charges a flat 2 credits per AI Writer run, no matter how many words. Off: POP keeps the key saved but pays with credits as usual.</span>
        </span>
      </div>

      <div class="card-actions">
        <button class="btn-save" type="button" :disabled="!cards[p.id].dirty || cards[p.id].saving" @click="saveCard(p)">Save changes</button>
        <span class="dirty-hint" v-if="cards[p.id].dirty">Not saved yet</span>
        <span class="save-flash" :class="{ show: cards[p.id].flash }">{{ cards[p.id].flash || '✓ Saved' }}</span>
        <button class="btn-link-danger" type="button" @click="cards[p.id].confirmOpen = true">Remove key</button>
      </div>

      <div class="confirm" v-if="cards[p.id].confirmOpen">
        <span class="cf-txt">Remove this key? AI features go back to standard POP credit pricing. You can add a key again later.</span>
        <span class="cf-actions">
          <button class="btn-del-no" type="button" @click="cards[p.id].confirmOpen = false">Keep it</button>
          <button class="btn-del-yes" type="button" @click="removeKey(p)">Remove key</button>
        </span>
      </div>
    </div>
  </section>

  <!-- Default models -->
  <section class="card">
    <div class="card-head"><h3>Starting model</h3></div>
    <p class="card-sub">What POP picks by default. You can change it on any individual article in AI Writer setup, so this is only the starting point.</p>
    <div class="model-grid">
      <div>
        <label class="field-label" for="claudeModel">Claude</label>
        <select class="select-input" id="claudeModel" v-model="selectedModels.anthropic" @change="onModelChange">
          <option v-for="m in modelOptions('anthropic')" :key="m.id" :value="m.id">{{ m.text }}</option>
        </select>
      </div>
      <div>
        <label class="field-label" for="openaiModel">GPT</label>
        <select class="select-input" id="openaiModel" v-model="selectedModels.openai" @change="onModelChange">
          <option v-for="m in modelOptions('openai')" :key="m.id" :value="m.id">{{ m.text }}</option>
        </select>
      </div>
    </div>
    <p class="pick-help">If you're not sure, leave both on <strong>recommended</strong>. Only current models are listed, and POP updates the list when a new one is released.</p>
    <div class="card-actions" style="margin-top:18px">
      <button class="btn-save" type="button" :disabled="!modelsDirty || modelsSaving" @click="saveModels">Save changes</button>
      <span class="dirty-hint" v-if="modelsDirty">Not saved yet</span>
      <span class="save-flash" :class="{ show: modelsFlash }">✓ Saved</span>
    </div>
  </section>
</div>
</template>

<script>
// One card per provider — the programmatic token stays 'openai' / 'anthropic';
// "Claude" appears only in display copy (design handoff).
const PROVIDERS = [
  {
    id: 'openai',
    badge: 'AI',
    logoColor: '#10A37F',
    keyLabel: 'OpenAI key',
    inputLabel: 'Secret key',
    modelsLabel: 'GPT',
    company: 'OpenAI',
    prefix: 'sk-',
    consoleUrl: 'https://platform.openai.com/api-keys',
    consoleLabel: 'platform.openai.com/api-keys',
    createWhat: 'a secret key',
    userKeyField: 'openAIKey',
  },
  {
    id: 'anthropic',
    badge: 'CL',
    logoColor: '#D97757',
    keyLabel: 'Claude key',
    inputLabel: 'API key',
    modelsLabel: 'Claude',
    company: 'Anthropic',
    prefix: 'sk-ant-',
    consoleUrl: 'https://console.anthropic.com/settings/keys',
    consoleLabel: 'console.anthropic.com',
    createWhat: 'an API key',
    userKeyField: 'anthropicKey',
  },
]

// Design handoff mask: first 7 chars + bullets + last 4.
function maskKey (v) {
  return v.length > 12 ? v.slice(0, 7) + '•'.repeat(Math.min(26, v.length - 11)) + v.slice(-4) : v
}

function freshCard () {
  return {
    real: '',        // the key text as typed / stored
    saved: false,    // a key is stored on the account
    toggleOn: false, // "Use this key for AI features"
    masked: false,   // input shows the masked form (readonly)
    dirty: false,    // unsaved key/toggle changes
    adding: false,   // empty-state "Add key" pressed (reveals the live field)
    bad: false,      // fails the prefix check
    msg: null,       // { kind: 'ok'|'err'|'warn', text }
    testing: false,
    saving: false,
    confirmOpen: false,
    flash: '',       // '✓ Saved' | '✓ Key removed'
    flashTimer: null,
  }
}

export default {
  name: 'AiAccessKeys',
  data () {
    return {
      providers: PROVIDERS,
      cards: { openai: freshCard(), anthropic: freshCard() },
      selectedModels: { openai: '', anthropic: '' },
      initialModels: { openai: '', anthropic: '' },
      modelsSaving: false,
      modelsFlash: false,
      modelsFlashTimer: null,
    }
  },
  computed: {
    user () {
      return this.$store.state.user || {}
    },
    modelCatalog () {
      return (this.$store.getters.getPOPCharges || {}).aiModelCatalog || {}
    },
    // Providers whose key the account actively bills (design: drives the
    // strip, updating live as toggles change — same as the handoff script's
    // `saved && has && on`).
    activeLabels () {
      return this.providers
        .filter(p => {
          const c = this.cards[p.id]
          return c.saved && !!c.real && c.toggleOn
        })
        .map(p => (p.id === 'anthropic' ? 'Claude' : 'OpenAI'))
    },
    stripTitle () {
      const active = this.activeLabels
      if (!active.length) return 'AI features are paid with POP credits'
      if (active.length === 2) return 'Your OpenAI and Claude keys are in use'
      return 'Your ' + active[0] + ' key is in use'
    },
    stripSub () {
      if (!this.activeLabels.length) {
        return 'Nothing to set up. Add a key below only if you want OpenAI or Anthropic to bill you for usage instead.'
      }
      // On a Claude key, images still run on an OpenAI model and keep their
      // 1-credit-each price — say so whenever the Claude key is the active one.
      const imageNote = this.activeLabels.includes('Claude')
        ? ' (plus 1 credit per image, since images still use an OpenAI model)'
        : ''
      return 'AI features run on your own key, so the AI company bills you for usage and POP charges a flat 2 credits per AI Writer run, no matter how long the article' + imageNote + '. Schema generation costs 0 credits. Turn the key off below to go back to normal credit pricing.'
    },
    modelsDirty () {
      return this.selectedModels.openai !== this.initialModels.openai
        || this.selectedModels.anthropic !== this.initialModels.anthropic
    },
  },
  methods: {
    // ----- state sync ------------------------------------------------------
    storedKey (p) {
      const value = this.user[p.userKeyField]
      // Sub-account relations serialize keys as booleans, never the raw value.
      return typeof value === 'string' ? value : ''
    },
    syncFromUser () {
      const user = this.user
      this.providers.forEach(p => {
        const c = this.cards[p.id]
        const stored = this.storedKey(p)
        c.real = stored
        c.saved = !!stored
        c.toggleOn = !!(user.useMyOwnApiKey && (user.ownApiKeyProvider || 'openai') === p.id && stored)
        // The design shows the stored key unmasked on load ("Hide" ready).
        c.masked = false
        c.dirty = false
        c.adding = false
        c.bad = false
        c.msg = null
        c.confirmOpen = false
      })
      const catalog = this.modelCatalog
      this.providers.forEach(p => {
        const list = catalog[p.id] || []
        const recommended = (list.find(m => m.recommended) || {}).id || ''
        const preferredField = p.id === 'anthropic' ? 'preferredAnthropicModel' : 'preferredOpenaiModel'
        const preferred = typeof user[preferredField] === 'string' ? user[preferredField] : ''
        const value = list.some(m => m.id === preferred) ? preferred : recommended
        this.$set(this.selectedModels, p.id, value)
        this.$set(this.initialModels, p.id, value)
      })
    },
    reloadUser () {
      return this.$store.dispatch('loadUser').then(response => {
        if (response && response.data && response.data.user) {
          this.$store.commit('setUser', response.data.user)
        }
        this.syncFromUser()
      })
    },
    // ----- per-card behavior (ported from the design handoff script) -------
    showLive (providerId) {
      const c = this.cards[providerId]
      return !!(c.real || c.dirty || c.adding)
    },
    stateChip (providerId) {
      // Same ladder as the handoff script: Not added / Not saved / In use /
      // Saved, paused. Dirty edits keep the current chip (the "Not saved yet"
      // hint next to Save carries that signal).
      const c = this.cards[providerId]
      const has = !!c.real
      if (!has) return { label: 'Not added', cls: '' }
      if (!c.saved) return { label: 'Not saved', cls: '' }
      if (c.toggleOn) return { label: 'In use', cls: 'on' }
      return { label: 'Saved, paused', cls: 'paused' }
    },
    displayValue (providerId) {
      const c = this.cards[providerId]
      return c.masked ? maskKey(c.real) : c.real
    },
    focusInput (p) {
      this.$nextTick(() => {
        const el = this.$refs[p.id + 'Input']
        const input = Array.isArray(el) ? el[0] : el
        if (input) input.focus()
      })
    },
    startAdding (p) {
      this.cards[p.id].adding = true
      this.focusInput(p)
    },
    clearFlash (c) {
      c.flash = ''
      if (c.flashTimer) { clearTimeout(c.flashTimer); c.flashTimer = null }
    },
    onKeyInput (p, event) {
      const c = this.cards[p.id]
      c.real = (event.target.value || '').trim()
      c.masked = false
      c.bad = false
      c.msg = null
      if (c.real && !c.real.startsWith(p.prefix)) {
        c.bad = true
        c.msg = { kind: 'err', text: "That doesn't look like a " + (p.id === 'anthropic' ? 'Claude' : 'OpenAI') + ' key. ' + (p.id === 'anthropic' ? 'Claude' : 'OpenAI') + ' keys start with ' + p.prefix }
      }
      if (!c.real) c.toggleOn = false
      c.dirty = true
      this.clearFlash(c)
    },
    toggleMask (p) {
      const c = this.cards[p.id]
      if (!c.real) return
      c.masked = !c.masked
    },
    onToggle (p) {
      const c = this.cards[p.id]
      if (!c.real) { this.focusInput(p); return }
      c.toggleOn = !c.toggleOn
      c.dirty = true
      this.clearFlash(c)
    },
    testKey (p) {
      const c = this.cards[p.id]
      if (!c.real || c.testing) return
      c.testing = true
      c.msg = null
      this.$store.dispatch('testAiKey', { provider: p.id, apiKey: c.real })
        .then(response => {
          const ok = response && response.data && response.data.status === 'SUCCESS'
          c.msg = ok
            ? { kind: 'ok', text: 'Key works. POP reached ' + p.company + ' successfully.' }
            : { kind: 'err', text: "POP couldn't use this key. Check you copied the whole thing, then try again." }
        })
        .catch(() => {
          c.msg = { kind: 'err', text: "POP couldn't use this key. Check you copied the whole thing, then try again." }
        })
        .finally(() => { c.testing = false })
    },
    saveCard (p) {
      const c = this.cards[p.id]
      if (c.real && !c.real.startsWith(p.prefix)) { this.focusInput(p); return }
      c.saving = true
      c.msg = null
      this.$store.dispatch('manageAiKey', {
        provider: p.id,
        apiKey: c.real,
        useMyOwnAPI: c.toggleOn,
      })
        .then(response => {
          const ok = response && response.data && response.data.status === 'SUCCESS'
          if (ok) {
            return this.reloadUser().then(() => { this.showFlash(p.id, '✓ Saved') })
          }
          // A failed validation clears the stored key server-side; keep the
          // typed key + error on screen so the user can fix and retry.
          c.saved = false
          c.msg = { kind: 'err', text: (response && response.data && response.data.error) || 'Something went wrong. Please try again.' }
        })
        .catch(() => {
          c.msg = { kind: 'err', text: 'Something went wrong. Please try again.' }
        })
        .finally(() => { c.saving = false })
    },
    removeKey (p) {
      const c = this.cards[p.id]
      c.confirmOpen = false
      if (!c.saved) {
        // Nothing stored yet — clear locally (back to the empty state).
        this.clearFlash(c)
        Object.assign(c, freshCard())
        return
      }
      c.saving = true
      this.$store.dispatch('manageAiKey', { provider: p.id, deleteKey: true })
        .then(() => this.reloadUser())
        .then(() => { this.showFlash(p.id, '✓ Key removed') })
        .finally(() => { c.saving = false })
    },
    showFlash (providerId, text) {
      const c = this.cards[providerId]
      this.clearFlash(c)
      c.flash = text
      c.flashTimer = setTimeout(() => { c.flash = '' }, 2400)
    },
    // ----- starting models -------------------------------------------------
    modelOptions (providerId) {
      const list = this.modelCatalog[providerId] || []
      return list.map(m => ({ id: m.id, text: m.recommended ? m.label + ' — recommended' : m.label }))
    },
    onModelChange () {
      this.modelsFlash = false
      if (this.modelsFlashTimer) { clearTimeout(this.modelsFlashTimer); this.modelsFlashTimer = null }
    },
    saveModels () {
      this.modelsSaving = true
      this.$store.dispatch('updateAiModels', {
        preferredOpenaiModel: this.selectedModels.openai,
        preferredAnthropicModel: this.selectedModels.anthropic,
      })
        .then(response => {
          if (response && response.data && response.data.status === 'SUCCESS') {
            return this.reloadUser().then(() => {
              this.modelsFlash = true
              this.modelsFlashTimer = setTimeout(() => { this.modelsFlash = false }, 2400)
            })
          }
        })
        .finally(() => { this.modelsSaving = false })
    },
  },
  watch: {
    // The user object / model catalog can load after mount (direct deep link);
    // resync as long as nothing is being edited.
    'user.id' () {
      if (!this.providers.some(p => this.cards[p.id].dirty) && !this.modelsDirty) {
        this.syncFromUser()
      }
    },
    modelCatalog () {
      if (!this.providers.some(p => this.cards[p.id].dirty) && !this.modelsDirty) {
        this.syncFromUser()
      }
    },
  },
  mounted () {
    this.syncFromUser()
  },
  beforeDestroy () {
    this.providers.forEach(p => this.clearFlash(this.cards[p.id]))
    if (this.modelsFlashTimer) clearTimeout(this.modelsFlashTimer)
  },
}
</script>

<style lang="scss" scoped>
/* Ported verbatim from the design handoff (POP Account Settings · AI access
   keys standalone mockup); scoped to the pane — the topbar/side-nav come from
   UserProfile. Adaptations: font-family inherits the app font, and the design
   tokens live on the component root instead of :root. */
.ai-access-keys {
  --pop-orange:#FF5700; --pop-orange-hover:#E64E00; --pop-orange-soft:#FFEDDD;
  --pop-green:#25A97A; --pop-green-soft:#EAFBF3; --pop-green-line:#C8EFD9;
  --pop-blue:#2563EB; --pop-blue-soft:#EAF1FE;
  --amber:#B4690E; --amber-soft:#FDF3E3; --amber-line:#F3DFBD;
  --red:#F0333F; --red-hover:#D62732; --red-soft:#FDECED;
  --bg:#FFFFFF; --surface:#FFFFFF; --surface-2:#F7F7F7; --border:#E4E4E4; --border-strong:#CCCCCC;
  --text:#052E33; --text-2:#014751; --text-3:#6B7B7E; --text-muted:#9AA6A8;

  max-width: 900px;
  color: var(--text);

  /* The mockup relies on the browser's default anchor underline; Bulma strips
     it globally, so declare it explicitly. */
  a { color: var(--pop-blue); text-decoration: underline; }
  a:hover { color: #1D4ED8; }
  /* Bulma's global `strong { color: #363636 }` would render emphasis near-black
     regardless of context; keep it in the surrounding text color (design). */
  strong { color: inherit; font-weight: 700; }

  .lede { font-size: 14px; color: var(--text-3); margin: 0 0 22px; line-height: 1.6; max-width: 70ch; }

  /* "Right now" status strip */
  .status-strip { display: flex; align-items: flex-start; gap: 13px; border: 1px solid var(--pop-green-line); background: var(--pop-green-soft); border-radius: 11px; padding: 15px 17px; margin-bottom: 14px; }
  .status-strip.credits { border-color: var(--border); background: var(--surface-2); }
  .status-strip .ss-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--pop-green); flex: none; margin-top: 6px; }
  .status-strip.credits .ss-dot { background: var(--text-muted); }
  .status-strip .ss-title { font-size: 14.5px; font-weight: 800; margin-bottom: 3px; }
  .status-strip .ss-sub { font-size: 13px; color: var(--text-2); line-height: 1.55; }

  /* Explainer (explicit white body — the pane can sit on a tinted page bg) */
  .explainer { border: 1px solid var(--border); border-radius: 11px; padding: 0; margin-bottom: 26px; overflow: hidden; background: var(--surface); }
  .explainer summary { list-style: none; cursor: pointer; display: flex; align-items: center; gap: 9px; padding: 13px 17px; font-size: 13.5px; font-weight: 700; color: var(--text-2); background: var(--surface-2); }
  .explainer summary::-webkit-details-marker { display: none; }
  .explainer summary .chev { margin-left: auto; transition: transform .18s; line-height: 0; }
  .explainer summary .chev svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
  .explainer[open] summary .chev { transform: rotate(180deg); }
  .explainer .ex-body { padding: 17px 19px 19px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  .ex-col h4 { font-size: 13.5px; font-weight: 800; margin: 0 0 8px; display: flex; align-items: center; gap: 7px; color: var(--text); }
  .ex-col ul { margin: 0; padding-left: 18px; font-size: 13px; color: var(--text-2); line-height: 1.65; }
  .ex-col li { list-style: disc; }
  .ex-col li + li { margin-top: 4px; }

  /* Card */
  /* color/background declared explicitly — Bulma's global .card would
     otherwise tint the text (#4a4a4a). */
  .card { background: var(--surface); border: 1px solid var(--border); border-radius: 11px; padding: 20px 22px 22px; margin-bottom: 18px; box-shadow: 0 1px 2px rgba(1,71,81,.04); color: var(--text); }
  .card-head { display: flex; align-items: center; gap: 11px; margin-bottom: 6px; }
  .card-logo { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; color: #fff; flex: none; letter-spacing: -.2px; }
  .card-head h3 { font-size: 17.5px; font-weight: 800; margin: 0; color: var(--text); }
  .card-state { margin-left: auto; font-size: 12px; font-weight: 700; padding: 4px 11px; border-radius: 999px; border: 1px solid var(--border-strong); color: var(--text-3); background: #fff; white-space: nowrap; }
  .card-state.on { color: var(--pop-green); border-color: var(--pop-green-line); background: var(--pop-green-soft); }
  .card-state.paused { color: var(--amber); border-color: var(--amber-line); background: var(--amber-soft); }
  .card-sub { font-size: 13px; color: var(--text-3); margin: 0 0 16px; line-height: 1.55; max-width: 72ch; }

  /* Empty state */
  .empty { border: 1px dashed var(--border-strong); border-radius: 10px; background: var(--surface-2); padding: 18px 19px; }
  .empty h4 { font-size: 14px; font-weight: 800; margin: 0 0 7px; color: var(--text); }
  .empty p { font-size: 13px; color: var(--text-2); line-height: 1.6; margin: 0 0 14px; max-width: 66ch; }
  .empty .steps { display: flex; flex-direction: column; gap: 7px; margin: 0 0 16px; }
  .empty .step { display: flex; gap: 9px; font-size: 13px; color: var(--text-2); line-height: 1.5; }
  .empty .step .n { flex: none; width: 19px; height: 19px; border-radius: 50%; background: #fff; border: 1px solid var(--border-strong); font-size: 11px; font-weight: 800; display: flex; align-items: center; justify-content: center; color: var(--text-3); margin-top: 1px; }
  .btn-add { background: #fff; color: var(--text-2); border: 1px solid var(--text-2); border-radius: 100px; padding: 10px 20px; font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer; transition: border-color .15s, color .15s; }
  .btn-add:hover { border-color: var(--pop-orange); color: var(--pop-orange); }

  /* Field */
  .field { margin-bottom: 4px; }
  .field > label { display: block; font-size: 12.5px; font-weight: 800; color: var(--text-2); margin-bottom: 6px; }
  /* NOT named .row — Vuetify's global .row (flex-wrap + -12px margins) would
     bleed in and make the input overflow the card / overlap the label. */
  .field .key-row { display: flex; flex-wrap: nowrap; margin: 0; gap: 8px; align-items: center; }
  .key-input { flex: 1; min-width: 0; background: #fff; border: 1px solid var(--border-strong); border-radius: 8px; padding: 12px 14px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--text); transition: border-color .15s, box-shadow .15s; }
  .key-input:focus { outline: none; border-color: var(--pop-orange); box-shadow: 0 0 0 3px var(--pop-orange-soft); }
  .key-input::placeholder { font-family: inherit; color: var(--text-muted); }
  .key-input.bad { border-color: var(--red); }
  .btn-ghost { background: #fff; border: 1px solid var(--border-strong); color: var(--text-2); border-radius: 8px; padding: 11px 14px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; flex: none; }
  .btn-ghost:hover { border-color: var(--text-3); color: var(--text); }
  .btn-ghost:disabled { opacity: .45; cursor: default; }
  .field-help { font-size: 12.5px; color: var(--text-3); margin-top: 8px; line-height: 1.55; }
  .inline-msg { display: flex; align-items: flex-start; gap: 8px; font-size: 12.5px; font-weight: 600; line-height: 1.5; margin-top: 9px; padding: 9px 11px; border-radius: 8px; }
  .inline-msg.ok { color: #15805C; background: var(--pop-green-soft); border: 1px solid var(--pop-green-line); }
  .inline-msg.warn { color: var(--amber); background: var(--amber-soft); border: 1px solid var(--amber-line); }
  .inline-msg.err { color: var(--red-hover); background: var(--red-soft); border: 1px solid #F6CFD2; }
  .inline-msg .ic { flex: none; font-weight: 800; }

  /* Toggle */
  .toggle-row { display: flex; align-items: flex-start; gap: 12px; margin: 18px 0; padding: 13px 14px; border: 1px solid var(--border); border-radius: 9px; background: #fff; }
  .toggle-row.off { background: var(--surface-2); }
  .toggle { width: 44px; height: 24px; border-radius: 100px; border: none; background: var(--border-strong); position: relative; cursor: pointer; flex: none; transition: background .18s; padding: 0; margin-top: 1px; }
  .toggle::after { content: ""; position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.2); transition: transform .18s; }
  .toggle.on { background: var(--pop-green); }
  .toggle.on::after { transform: translateX(20px); }
  .toggle-row .tl { font-size: 14px; font-weight: 800; display: block; margin-bottom: 2px; color: var(--text); }
  .toggle-row .th { font-size: 12.5px; color: var(--text-3); line-height: 1.5; }

  /* Actions */
  .card-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  .btn-save { background: #fff; color: var(--text-2); border: 1px solid var(--text-2); border-radius: 100px; padding: 11px 24px; font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer; transition: border-color .15s, color .15s; }
  .btn-save:hover { border-color: var(--pop-orange); color: var(--pop-orange); }
  .btn-save:disabled { background: #fff; border-color: var(--border); color: var(--text-muted); cursor: default; }
  .btn-link-danger { background: none; border: none; color: var(--red-hover); font-family: inherit; font-size: 13.5px; font-weight: 700; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; padding: 6px 4px; margin-left: auto; }
  .btn-link-danger:hover { color: var(--red); }
  .dirty-hint { font-size: 12.5px; font-weight: 700; color: var(--amber); }
  .save-flash { font-size: 13px; font-weight: 700; color: var(--pop-green); display: inline-flex; align-items: center; gap: 6px; opacity: 0; transition: opacity .2s; }
  .save-flash.show { opacity: 1; }
  .confirm { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 14px; padding: 13px 15px; border: 1px solid #F6CFD2; background: var(--red-soft); border-radius: 9px; }
  .confirm .cf-txt { font-size: 13px; color: #8A2027; font-weight: 600; line-height: 1.5; }
  .confirm .cf-actions { margin-left: auto; display: flex; gap: 8px; }
  .btn-del-yes { background: var(--red); color: #fff; border: none; border-radius: 100px; padding: 8px 16px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
  .btn-del-yes:hover { background: var(--red-hover); }
  .btn-del-no { background: #fff; border: 1px solid var(--border-strong); color: var(--text-2); border-radius: 100px; padding: 8px 16px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }

  /* Default models */
  .model-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  /* text-align/flex/margin declared explicitly — Bulma's global .field-label
     (flex-grow + right-aligned + 1.5rem margin) would push these off-left. */
  .field-label { display: block; font-size: 13px; font-weight: 800; margin: 0 0 7px; color: var(--text); text-align: left; flex: none; }
  .select-input { width: 100%; background: #fff; border: 1px solid var(--border-strong); border-radius: 8px; padding: 11px 34px 11px 13px; font-family: inherit; font-size: 13.5px; color: var(--text); appearance: none; cursor: pointer; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%236B7280' d='M5 6L0 0h10z'/></svg>"); background-repeat: no-repeat; background-position: right 13px center; }
  .select-input:focus { outline: none; border-color: var(--pop-orange); box-shadow: 0 0 0 3px var(--pop-orange-soft); }
  .pick-help { font-size: 12.5px; color: var(--text-3); margin: 16px 0 0; line-height: 1.6; }
  .pick-help strong { color: var(--text-2); }
}

@media (max-width: 1080px) {
  .ai-access-keys {
    .model-grid, .explainer .ex-body { grid-template-columns: 1fr; }
  }
}
</style>
